import React, { useEffect, useRef, useState } from "react";
import { Share2, Linkedin, MessageCircle, Link as LinkIcon, X } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** ShareButton — opens a tiny popover with platform-specific intent links.
 *
 * Props:
 *  - slug  : share-cards slug (e.g. "brutal-design", "plate-vogue", "monologue")
 *  - label : button label, default "Share"
 *  - title : preview/composer text (falls back to card title from /share-cards)
 *  - className : passthrough for sizing
 */
export default function ShareButton({ slug, label = "Share", title, className = "", testId }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [meta, setMeta] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    let alive = true;
    if (!open || meta) return;
    axios.get(`${API}/share-cards`)
      .then((r) => {
        if (!alive) return;
        const card = r.data?.cards?.[slug] || r.data?.cards?.default;
        setMeta({
          base: r.data?.base_url || window.location.origin,
          card,
        });
      })
      .catch(() => alive && setMeta({ base: window.location.origin, card: null }));
    return () => { alive = false; };
  }, [open, slug, meta]);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const shareUrl = meta ? `${meta.base.replace(/\/$/, "")}/api/share/${slug}` : "";
  const previewTitle = title || meta?.card?.title || "Ranjana Baruah — portfolio";

  const intents = shareUrl ? [
    {
      key: "linkedin",
      icon: <Linkedin size={14} />,
      label: "LinkedIn",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    },
    {
      key: "whatsapp",
      icon: <MessageCircle size={14} />,
      label: "WhatsApp",
      href: `https://wa.me/?text=${encodeURIComponent(`${previewTitle} — ${shareUrl}`)}`,
    },
  ] : [];

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };

  const handleNative = async () => {
    if (!navigator.share) {
      setOpen((v) => !v);
      return;
    }
    try {
      await navigator.share({ title: previewTitle, url: shareUrl });
    } catch { /* user cancelled */ }
  };

  return (
    <span ref={ref} className={`relative inline-flex ${className}`}>
      <button
        type="button"
        onClick={() => navigator.share && shareUrl ? handleNative() : setOpen((v) => !v)}
        data-testid={testId || `share-btn-${slug}`}
        aria-haspopup="menu"
        aria-expanded={open}
        className="font-mono text-[10px] tracking-[0.25em] uppercase border border-white/20 px-3 py-1.5 hover:bg-white hover:text-black transition-all inline-flex items-center gap-2 backdrop-blur-sm bg-black/40"
      >
        <Share2 size={12} /> {label}
      </button>
      {open && (
        <div
          role="menu"
          data-testid={`share-menu-${slug}`}
          className="absolute z-50 right-0 top-full mt-2 w-56 bg-[#0a0a0a] border border-white/15 shadow-2xl"
        >
          <div className="flex items-center justify-between p-3 border-b border-white/10">
            <span className="font-mono text-[9px] tracking-[0.3em] uppercase text-[#a3a3a3]">Share to</span>
            <button onClick={() => setOpen(false)} aria-label="Close share menu" className="text-[#a3a3a3] hover:text-white">
              <X size={12} />
            </button>
          </div>
          <ul className="divide-y divide-white/5">
            {intents.map((it) => (
              <li key={it.key}>
                <a
                  data-testid={`share-${it.key}-${slug}`}
                  href={it.href}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-3 px-4 py-3 font-mono text-[10px] tracking-[0.25em] uppercase text-[#f5f5f5] hover:bg-white hover:text-black transition-colors"
                >
                  {it.icon} {it.label}
                </a>
              </li>
            ))}
            <li>
              <button
                onClick={handleCopy}
                data-testid={`share-copy-${slug}`}
                className="w-full flex items-center gap-3 px-4 py-3 font-mono text-[10px] tracking-[0.25em] uppercase text-[#f5f5f5] hover:bg-white hover:text-black transition-colors"
              >
                <LinkIcon size={14} /> {copied ? "Copied!" : "Copy link"}
              </button>
            </li>
          </ul>
          {shareUrl && (
            <p className="px-4 py-2 font-mono text-[8px] tracking-[0.2em] uppercase text-[#666] truncate" title={shareUrl}>
              {shareUrl}
            </p>
          )}
        </div>
      )}
    </span>
  );
}
